/**
 * Internal-only post-auth redirects. Rejects open redirects to other origins.
 */

const RETURN_AFTER_AUTH_KEY = 'return_after_auth';
const APP_ORIGIN = 'https://app.greatestgiftapp.com';

const RETURNABLE_PATH_PREFIXES = [
  '/settings/account/membership',
  '/give-a-gift',
  '/monthly-matching',
  '/plus/pass',
  // Plan pages, including gift matching, so a session timeout can return here after login.
  '/plan',
];

/**
 * Return a same-app path (pathname + search + hash) or null.
 * Allows /foo?bar=baz; rejects //host, protocols, backslashes, credentials.
 */
export function safeInternalPath(value) {
  if (!value || typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  if (trimmed.includes('\\')) return null;
  if (trimmed.includes('\n') || trimmed.includes('\r') || trimmed.includes('\t')) return null;

  try {
    const url = new URL(trimmed, APP_ORIGIN);
    if (url.origin !== APP_ORIGIN) return null;
    if (url.username || url.password) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (e) {
    return null;
  }
}

export function isReturnableLocation(location) {
  const pathname = (location && location.pathname) || '';
  return RETURNABLE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

/** Login URL that returns to the originally requested in-app location. */
export function loginPathWithReturn(location) {
  if (!location || !isReturnableLocation(location)) return '/login';
  const requested = `${location.pathname || ''}${location.search || ''}`;
  const safe = safeInternalPath(requested);
  if (!safe) return '/login';
  return `/login?return_to=${encodeURIComponent(safe)}`;
}


export function storeReturnAfterAuth(path) {
  const safe = safeInternalPath(path);
  if (!safe) return null;
  try {
    sessionStorage.setItem(RETURN_AFTER_AUTH_KEY, safe);
  } catch (e) {
    // ignore storage errors
  }
  return safe;
}

/** Read and clear the stored post-auth destination. */
export function consumeReturnAfterAuth() {
  try {
    const stored = sessionStorage.getItem(RETURN_AFTER_AUTH_KEY);
    sessionStorage.removeItem(RETURN_AFTER_AUTH_KEY);
    return safeInternalPath(stored);
  } catch (e) {
    return null;
  }
}

export function peekReturnAfterAuth() {
  try {
    return safeInternalPath(sessionStorage.getItem(RETURN_AFTER_AUTH_KEY));
  } catch (e) {
    return null;
  }
}
